import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface JobListing {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  posted: string;
}

interface Benefit {
  title: string;
  description: string;
  iconClass: string;
}

@Component({
  selector: 'app-careers',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="careers-page">
      <div class="container">
        <header class="page-header">
          <h1>Join Our Team</h1>
          <p class="subtitle">Build the future of industry intelligence with us</p>
        </header>

        <div class="culture-section">
          <h2>Our Culture</h2>
          <div class="culture-content">
            <p>
              At Industry Intelligence, we foster an environment of innovation,
              collaboration, and continuous learning. Our diverse team of experts
              is united by a shared passion for transforming how businesses
              leverage data and insights.
            </p>
            <div class="culture-stats">
              <div class="stat">
                <div class="stat-number">4.8/5</div>
                <div class="stat-label">Employee Satisfaction</div>
              </div>
              <div class="stat">
                <div class="stat-number">92%</div>
                <div class="stat-label">Retention Rate</div>
              </div>
              <div class="stat">
                <div class="stat-number">45+</div>
                <div class="stat-label">Nationalities</div>
              </div>
            </div>
          </div>
        </div>

        <div class="benefits-section">
          <h2>Benefits & Perks</h2>
          <div class="benefits-grid">
            <div class="benefit-card" *ngFor="let benefit of benefits">
              <i [class]="benefit.iconClass"></i>
              <h3>{{ benefit.title }}</h3>
              <p>{{ benefit.description }}</p>
            </div>
          </div>
        </div>

        <div class="jobs-section">
          <h2>Open Positions</h2>
          
          <div class="jobs-filters">
            <div class="filter-group">
              <label for="department">Department</label>
              <select id="department" [(ngModel)]="selectedDepartment">
                <option value="">All Departments</option>
                <option value="Engineering">Engineering</option>
                <option value="Product">Product</option>
                <option value="Data Science">Data Science</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>
            
            <div class="filter-group">
              <label for="location">Location</label>
              <select id="location" [(ngModel)]="selectedLocation">
                <option value="">All Locations</option>
                <option value="Los Angeles">Los Angeles</option>
                <option value="New York">New York</option>
                <option value="London">London</option>
                <option value="Remote">Remote</option>
              </select>
            </div>
          </div>

          <div class="jobs-list">
            <div class="job-card" *ngFor="let job of filteredJobs">
              <div class="job-header">
                <h3>{{ job.title }}</h3>
                <span class="job-type">{{ job.type }}</span>
              </div>
              
              <div class="job-details">
                <span class="department">
                  <i class="fas fa-briefcase"></i> {{ job.department }}
                </span>
                <span class="location">
                  <i class="fas fa-map-marker-alt"></i> {{ job.location }}
                </span>
                <span class="posted">
                  <i class="fas fa-calendar"></i> Posted {{ job.posted }}
                </span>
              </div>

              <p class="job-description">{{ job.description }}</p>

              <div class="requirements">
                <h4>Requirements:</h4>
                <ul>
                  <li *ngFor="let req of job.requirements">{{ req }}</li>
                </ul>
              </div>

              <button class="apply-button">Apply Now</button>
            </div>
          </div>
        </div>

        <div class="cta-section">
          <h2>Don't see the right role?</h2>
          <p>
            We're always looking for talented individuals to join our team.
            Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <button class="cta-button">Submit Your Resume</button>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .careers-page {
      padding: 4rem 0;
      background: var(--background-light);
    }

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1rem;
    }

    .page-header {
      text-align: center;
      margin-bottom: 4rem;
    }

    h1 {
      font-size: 2.5rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .subtitle {
      color: var(--text-secondary);
      font-size: 1.1rem;
    }

    section {
      margin-bottom: 4rem;
    }

    h2 {
      font-size: 2rem;
      color: var(--text-primary);
      margin-bottom: 2rem;
      text-align: center;
    }

    .culture-section {
      text-align: center;
      max-width: 900px;
      margin: 0 auto 4rem;
    }

    .culture-content p {
      font-size: 1.1rem;
      line-height: 1.6;
      color: var(--text-secondary);
      margin-bottom: 2rem;
    }

    .culture-stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 2rem;
      margin-top: 2rem;
    }

    .stat {
      text-align: center;
    }

    .stat-number {
      font-size: 2rem;
      font-weight: bold;
      color: var(--primary-color);
      margin-bottom: 0.5rem;
    }

    .stat-label {
      color: var(--text-secondary);
    }

    .benefits-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 2rem;
      margin-bottom: 4rem;
    }

    .benefit-card {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      text-align: center;
    }

    .benefit-card i {
      font-size: 2.5rem;
      color: var(--primary-color);
      margin-bottom: 1rem;
    }

    .benefit-card h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin-bottom: 1rem;
    }

    .benefit-card p {
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .jobs-filters {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .filter-group {
      display: grid;
      gap: 0.5rem;
    }

    label {
      color: var(--text-primary);
      font-weight: 500;
    }

    select {
      padding: 0.75rem;
      border: 1px solid var(--border-color);
      border-radius: 4px;
      font-size: 1rem;
      width: 100%;
      background: white;
    }

    .jobs-list {
      display: grid;
      gap: 1.5rem;
    }

    .job-card {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .job-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }

    .job-header h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
    }

    .job-type {
      background: var(--primary-light);
      color: var(--primary-color);
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .job-details {
      display: flex;
      gap: 1.5rem;
      margin-bottom: 1rem;
      color: var(--text-secondary);
      font-size: 0.875rem;
    }

    .job-details i {
      margin-right: 0.5rem;
      color: var(--primary-color);
    }

    .job-description {
      color: var(--text-secondary);
      line-height: 1.6;
      margin-bottom: 1.5rem;
    }

    .requirements h4 {
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .requirements ul {
      list-style: disc;
      margin-left: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .requirements li {
      color: var(--text-secondary);
      line-height: 1.6;
      margin-bottom: 0.5rem;
    }

    .apply-button {
      background: var(--primary-color);
      color: white;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    .apply-button:hover {
      background: var(--primary-dark);
    }

    .cta-section {
      text-align: center;
      max-width: 600px;
      margin: 4rem auto 0;
    }

    .cta-section p {
      color: var(--text-secondary);
      margin-bottom: 2rem;
    }

    .cta-button {
      background: var(--primary-color);
      color: white;
      padding: 1rem 2rem;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    .cta-button:hover {
      background: var(--primary-dark);
    }

    @media (max-width: 768px) {
      .page-header {
        margin-bottom: 3rem;
      }

      h1 {
        font-size: 2rem;
      }

      h2 {
        font-size: 1.5rem;
      }

      .culture-stats {
        grid-template-columns: 1fr;
        gap: 1.5rem;
      }

      .job-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
      }

      .job-details {
        flex-direction: column;
        gap: 0.5rem;
      }
    }

    @media (max-width: 576px) {
      .careers-page {
        padding: 2rem 0;
      }

      .job-card {
        padding: 1.5rem;
      }
    }
  `]
})
export class CareersComponent {
  selectedDepartment = '';
  selectedLocation = '';

  benefits: Benefit[] = [
    {
      title: 'Health & Wellness',
      description: 'Comprehensive medical, dental, and vision coverage for you and your family.',
      iconClass: 'fas fa-heart'
    },
    {
      title: 'Work-Life Balance',
      description: 'Flexible working hours, remote work options, and generous PTO policy.',
      iconClass: 'fas fa-balance-scale'
    },
    {
      title: 'Professional Growth',
      description: 'Learning stipend, conference attendance, and career development programs.',
      iconClass: 'fas fa-graduation-cap'
    },
    {
      title: 'Financial Benefits',
      description: 'Competitive salary, equity options, and 401(k) matching.',
      iconClass: 'fas fa-piggy-bank'
    },
    {
      title: 'Team Events',
      description: 'Regular team building activities, social events, and company retreats.',
      iconClass: 'fas fa-users'
    },
    {
      title: 'Office Perks',
      description: 'Modern offices, snacks and beverages, and ergonomic workstations.',
      iconClass: 'fas fa-coffee'
    }
  ];

  jobs: JobListing[] = [
    {
      id: 'eng-001',
      title: 'Senior Full Stack Engineer',
      department: 'Engineering',
      location: 'Los Angeles',
      type: 'Full-time',
      description: 'We are seeking a Senior Full Stack Engineer to join our engineering team and help build the next generation of our industry intelligence platform.',
      requirements: [
        '7+ years of experience in full stack development',
        'Expertise in Angular, Node.js, and TypeScript',
        'Experience with cloud platforms (AWS/Azure)',
        'Strong understanding of software architecture and design patterns',
        'Excellent problem-solving and communication skills'
      ],
      posted: '2 days ago'
    },
    {
      id: 'ds-001',
      title: 'Lead Data Scientist',
      department: 'Data Science',
      location: 'New York',
      type: 'Full-time',
      description: 'Join our data science team to develop and implement advanced analytics solutions that drive business insights for our clients.',
      requirements: [
        'Ph.D. or M.S. in Computer Science, Statistics, or related field',
        'Expertise in machine learning and statistical modeling',
        'Experience with Python, R, and SQL',
        'Strong background in NLP and time series analysis',
        'Proven track record of leading data science projects'
      ],
      posted: '1 week ago'
    },
    {
      id: 'prod-001',
      title: 'Product Manager',
      department: 'Product',
      location: 'Remote',
      type: 'Full-time',
      description: 'We are looking for a Product Manager to drive the strategy and execution of our core analytics products.',
      requirements: [
        '5+ years of product management experience',
        'Strong background in B2B SaaS products',
        'Experience with data analytics or business intelligence tools',
        'Excellent stakeholder management skills',
        'MBA or equivalent experience preferred'
      ],
      posted: '3 days ago'
    },
    {
      id: 'sales-001',
      title: 'Enterprise Sales Director',
      department: 'Sales',
      location: 'London',
      type: 'Full-time',
      description: 'Drive enterprise sales growth in the European market by developing and executing strategic sales plans.',
      requirements: [
        '8+ years of enterprise software sales experience',
        'Proven track record of closing large enterprise deals',
        'Experience in data analytics or business intelligence industry',
        'Strong network in European markets',
        'Excellent presentation and negotiation skills'
      ],
      posted: '5 days ago'
    }
  ];

  get filteredJobs(): JobListing[] {
    return this.jobs.filter(job => {
      const matchesDepartment = !this.selectedDepartment || job.department === this.selectedDepartment;
      const matchesLocation = !this.selectedLocation || job.location === this.selectedLocation;
      return matchesDepartment && matchesLocation;
    });
  }
} 